import type { Permission } from '@kortex/shared';
import { isOpfsSqliteSupported } from '@kortex/pos-local/browser';
import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Loading } from './components/ui';
import { isAndroidKiosk } from './lib/androidBridge';
import { apiUrl } from './lib/api';
import { useAuth } from './lib/auth';
import { persistedDeviceId } from './lib/deviceId';
import { registrationRequired } from './lib/deviceAccess';
import { useKioskAccess } from './lib/useKioskAccess';
import { browserOfflineModeEnabled } from './lib/posData';
import { DeviceSetup, useDeviceRegistration } from './pages/DeviceSetup';
import { OfflineUnlock } from './pages/OfflineUnlock';
import { DiagnosticsPage } from './pages/Diagnostics';
import { DeviceManagementPage } from './pages/DeviceManagement';
import { AuditPage, SettingsPage, UsersPage } from './pages/Admin';
import { DashboardPage } from './pages/Dashboard';
import { FrontDeskPage } from './pages/FrontDesk';
import { GuestMenu } from './pages/GuestMenu';
import { GstPage, ReportsPage } from './pages/Insights';
import { InventoryPage } from './pages/Inventory';
import { InvoicesPage } from './pages/Invoices';
import { ExpensesPage } from './pages/Expenses';
import { DailyReportPage } from './pages/DailyReport';
import { DayBookPage } from './pages/DayBook';
import { ProfitAndLossPage } from './pages/ProfitAndLoss';
import { KitchenPage } from './pages/Kitchen';
import { MenuPage } from './pages/Menu';
import { OffersPage } from './pages/Offers';
import { PeoplePage } from './pages/People';
import { OnlineOrdersPage } from './pages/OnlineOrders';
import { PosPage } from './pages/Pos';
import { ProcurementPage } from './pages/Procurement';
import {
  PlatformDashboardPage,
  PlatformHotelsPage,
  PlatformPlansPage,
  PlatformReportsPage,
} from './pages/Platform';
import { PlatformCouponsPage } from './pages/Coupons';
import { PlatformLandingPage } from './pages/LandingContent';
import { PlatformBillingSettingsPage } from './pages/BillingAdmin';
import { BookRoom } from './pages/BookRoom';
import { ForgotPassword, ResetPassword } from './pages/Password';
import { PaymentSettingsPage } from './pages/PaymentSettings';
import { BillingPage } from './pages/Billing';
import { Terms } from './pages/Terms';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { PrivacyPage } from './pages/Privacy';
import { PlatformPrivacyPage } from './pages/PlatformPrivacy';
import {
  PlatformCaptchaPage,
  PlatformErrorsPage,
  PlatformMailPage,
} from './pages/PlatformSettings';
import { Register } from './pages/Register';
import { HousekeepingPage, ReservationsPage } from './pages/Reservations';
import { SignIn } from './pages/SignIn';

/** A route the signed-in role is not allowed to open. */
function NotAllowed() {
  return (
    <div className="card">
      <div className="empty">
        <span className="big">⚿</span>
        Your role does not have access to this screen.
      </div>
    </div>
  );
}

function Guard({ permission, children }: { permission: Permission; children: React.ReactNode }) {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <NotAllowed />;
}

/** Platform console routes; closed to every hotel role. */
function PlatformGuard({ children }: { children: React.ReactNode }) {
  const { isPlatformAdmin } = useAuth();
  return isPlatformAdmin ? <>{children}</> : <NotAllowed />;
}

/** The first screen a role can actually open, so nobody lands on a wall. */
function LandingRedirect() {
  const { can, isPlatformAdmin, session } = useAuth();
  // Kortex staff land in the console, not in a hotel.
  if (isPlatformAdmin && !session?.property) return <Navigate to="/platform" replace />;
  if (can('dashboard:view')) return <DashboardPage />;
  if (can('kds:view')) return <Navigate to="/kitchen" replace />;
  if (can('housekeeping:view')) return <Navigate to="/housekeeping" replace />;
  if (can('rooms:view')) return <Navigate to="/front-desk" replace />;
  return <NotAllowed />;
}

export function App() {
  const { status, session } = useAuth();
  // Subscribes this component to navigation. The public-page check further down
  // reads window.location, which on its own would only be looked at again on an
  // unrelated re-render: a footer link to /terms that stays inside the app (the
  // Android shell has no new tab to open it in) would change the URL and show nothing.
  useLocation();
  // Called unconditionally (react hook rules) - isAndroidKiosk() never
  // changes at runtime, and the query itself is a no-op until both kiosk and
  // status === 'authenticated' are true (see DeviceSetup.tsx).
  const kiosk = isAndroidKiosk();
  const deviceId = kiosk ? persistedDeviceId() : undefined;
  const registration = useDeviceRegistration(
    kiosk && status === 'authenticated' ? deviceId : undefined,
  );
  // How this device runs for this person (see lib/useKioskAccess.ts). Called
  // unconditionally like the hooks around it; it does nothing off the Android app.
  const access = useKioskAccess(kiosk && status === 'authenticated', session?.user.id, registration);

  // Phase 8: a device (browser tab or Android kiosk) that has completed
  // OfflineSetup and can still actually back it (isOpfsSqliteSupported() -
  // a browser update or a different machine could lose that support after
  // enrolling). Extended to the kiosk build too: it already runs on this
  // same OPFS driver for its always-on local data mode, and enrolling here
  // is what lets a cashier recover from an accidental sign-out while
  // offline instead of being locked out until the network returns.
  const offlineEligible = browserOfflineModeEnabled() && isOpfsSqliteSupported();
  // Defaults to the offline unlock screen when the tab is already known to
  // be offline at mount; SignIn's own "sign in offline instead" link (for a
  // browser that reports online but can't actually reach the server) and
  // OfflineUnlock's own "use online sign-in instead" link flip it either
  // way from there. A later 'offline' event (network dropped while SignIn
  // was already showing) also switches over - there is deliberately no
  // matching automatic switch back on 'online', so a cashier mid-PIN-entry
  // is never yanked back to the sign-in form underneath them.
  const [showOfflineUnlock, setShowOfflineUnlock] = useState(() => !navigator.onLine);
  useEffect(() => {
    const goOffline = () => setShowOfflineUnlock(true);
    window.addEventListener('offline', goOffline);
    return () => window.removeEventListener('offline', goOffline);
  }, []);
  // Set once somebody deliberately backs out of the offline screen
  // (OfflineUnlock's own "use online sign-in instead" link below) - a
  // choice the probe effect right after this must never overturn, the same
  // "no automatic switch back" rule the comment above already applies to a
  // real 'online' event.
  const onlineOverrideRef = useRef(false);
  // navigator.onLine and the 'offline' event above only ever reflect this
  // machine's own network interface - neither can see "the network is fine
  // but this one backend process is down", which is exactly the gap that
  // sent someone to the ordinary sign-in form (captcha "unavailable" and
  // all) instead of the PIN screen despite having offline access enabled.
  // Only meaningful pre-sign-in, so this is scoped to 'anonymous' rather
  // than running for the lifetime of an authenticated session; it stops
  // probing the moment showOfflineUnlock flips true (nothing left to
  // detect) or the user has explicitly overridden it above.
  useEffect(() => {
    if (status !== 'anonymous' || !offlineEligible || showOfflineUnlock || onlineOverrideRef.current) {
      return;
    }
    let cancelled = false;
    const SERVER_PROBE_TIMEOUT_MS = 4_000;
    const probe = async () => {
      try {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), SERVER_PROBE_TIMEOUT_MS);
        const res = await fetch(apiUrl('/api/health'), {
          signal: controller.signal,
          cache: 'no-store',
        });
        window.clearTimeout(timer);
        if (!cancelled && !res.ok) setShowOfflineUnlock(true);
      } catch {
        // Aborted (timed out), a network error, or the request never left -
        // every one of those means "could not reach the backend just now",
        // exactly the condition this probe exists to catch.
        if (!cancelled) setShowOfflineUnlock(true);
      }
    };
    void probe();
    // Re-checked periodically (not just once at mount) so a backend that
    // goes down WHILE the sign-in form is already showing is caught too,
    // not only a backend that was already down before this screen rendered.
    const SERVER_PROBE_INTERVAL_MS = 15_000;
    const intervalId = window.setInterval(() => void probe(), SERVER_PROBE_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [status, offlineEligible, showOfflineUnlock]);

  // A guest scanning a table code has no session and never will have one, so
  // this route is matched before anything asks who they are - including before
  // the app has finished restoring an owner's own session.
  if (
    window.location.pathname.startsWith('/m/') ||
    window.location.pathname.startsWith('/stay/') ||
    window.location.pathname === '/terms' ||
    window.location.pathname === '/privacy'
  ) {
    return (
      <Routes>
        <Route path="/m/:token" element={<GuestMenu />} />
        <Route path="/stay/:token" element={<BookRoom />} />
        {/* Readable by anybody, signed in or not: somebody deciding whether to
            register must be able to read what they would be agreeing to. */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
      </Routes>
    );
  }

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Loading label="Restoring your session…" />
      </div>
    );
  }

  if (status === 'anonymous') {
    if (offlineEligible && showOfflineUnlock) {
      return (
        <OfflineUnlock
          onUseOnlineSignIn={() => {
            // A deliberate choice to see the online form anyway (maybe they
            // want Forgot password/Register, or actually believe the
            // backend is back) - the probe effect above must not immediately
            // bounce them back the moment its next check fails again.
            onlineOverrideRef.current = true;
            setShowOfflineUnlock(false);
          }}
        />
      );
    }
    return (
      <Routes>
        <Route
          path="/sign-in"
          element={
            <SignIn
              offlineAvailable={offlineEligible}
              onUseOfflineInstead={() => setShowOfflineUnlock(true)}
            />
          }
        />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  // Phase 6 device configuration model (docs/android/PHASE-6-BASELINE-
  // AUDIT.md SS3): where registration is required, a kiosk tablet nobody has
  // registered yet is blocked from the ordinary app shell until an administrator
  // sets it up, rather than silently operating under whatever property its
  // cashier's session happened to default to.
  // Held until the server has said how this device runs for this person: the
  // till picks its data mode when it first mounts, and must not guess.
  if (kiosk && !access.ready) return <Loading label="Setting up this device…" />;

  // Only where registration is required of this person. The property decides, and
  // the owner is never held to it - the server answers `required` accordingly.
  // A device that is neither registered nor required runs online, like the website.
  if (
    kiosk &&
    deviceId &&
    registration.data &&
    registrationRequired(registration.data) &&
    !registration.data.registered
  ) {
    return <DeviceSetup deviceId={deviceId} onRegistered={() => void registration.refetch()} />;
  }

  return (
    <Routes>
      <Route path="/sign-in" element={<Navigate to="/" replace />} />
      <Route path="/register" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<Layout />}>
        <Route index element={<LandingRedirect />} />
        <Route
          path="pos"
          element={
            <Guard permission="pos:operate">
              <PosPage />
            </Guard>
          }
        />
        <Route path="billing" element={<BillingPage />} />
        <Route
          path="payments"
          element={
            <Guard permission="settings:manage">
              <PaymentSettingsPage />
            </Guard>
          }
        />
        <Route
          path="online-orders"
          element={
            <Guard permission="pos:operate">
              <OnlineOrdersPage />
            </Guard>
          }
        />
        <Route
          path="kitchen"
          element={
            <Guard permission="kds:view">
              <KitchenPage />
            </Guard>
          }
        />
        <Route
          path="front-desk"
          element={
            <Guard permission="rooms:view">
              <FrontDeskPage />
            </Guard>
          }
        />
        <Route
          path="reservations"
          element={
            <Guard permission="reservation:view">
              <ReservationsPage />
            </Guard>
          }
        />
        <Route
          path="housekeeping"
          element={
            <Guard permission="housekeeping:view">
              <HousekeepingPage />
            </Guard>
          }
        />
        <Route
          path="invoices"
          element={
            <Guard permission="invoice:view">
              <InvoicesPage />
            </Guard>
          }
        />
        <Route
          path="expenses"
          element={
            <Guard permission="expense:view">
              <ExpensesPage />
            </Guard>
          }
        />
        <Route
          path="day-book"
          element={
            <Guard permission="daybook:view">
              <DayBookPage />
            </Guard>
          }
        />
        <Route
          path="daily-report"
          element={
            <Guard permission="daybook:view">
              <DailyReportPage />
            </Guard>
          }
        />
        <Route
          path="pnl"
          element={
            <Guard permission="pnl:view">
              <ProfitAndLossPage />
            </Guard>
          }
        />
        <Route
          path="menu"
          element={
            <Guard permission="menu:view">
              <MenuPage />
            </Guard>
          }
        />
        <Route
          path="offers"
          element={
            <Guard permission="menu:view">
              <OffersPage />
            </Guard>
          }
        />
        <Route
          path="inventory"
          element={
            <Guard permission="inventory:view">
              <InventoryPage />
            </Guard>
          }
        />
        <Route
          path="procurement"
          element={
            <Guard permission="procurement:view">
              <ProcurementPage />
            </Guard>
          }
        />
        <Route
          path="people"
          element={
            <Guard permission="hr:view">
              <PeoplePage />
            </Guard>
          }
        />
        <Route
          path="reports"
          element={
            <Guard permission="reports:view">
              <ReportsPage />
            </Guard>
          }
        />
        <Route
          path="gst"
          element={
            <Guard permission="gst:export">
              <GstPage />
            </Guard>
          }
        />
        <Route
          path="users"
          element={
            <Guard permission="users:view">
              <UsersPage />
            </Guard>
          }
        />
        <Route
          path="settings"
          element={
            <Guard permission="settings:view">
              <SettingsPage />
            </Guard>
          }
        />
        <Route
          path="audit"
          element={
            <Guard permission="audit:view">
              <AuditPage />
            </Guard>
          }
        />
        <Route
          path="privacy-compliance"
          element={
            <Guard permission="privacy:view">
              <PrivacyPage />
            </Guard>
          }
        />
        <Route
          path="device-diagnostics"
          element={
            <Guard permission="property:manage">
              <DiagnosticsPage />
            </Guard>
          }
        />
        <Route
          path="device-management"
          element={
            <Guard permission="property:manage">
              <DeviceManagementPage />
            </Guard>
          }
        />
        <Route
          path="platform"
          element={
            <PlatformGuard>
              <PlatformDashboardPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/hotels"
          element={
            <PlatformGuard>
              <PlatformHotelsPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/plans"
          element={
            <PlatformGuard>
              <PlatformPlansPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/landing"
          element={
            <PlatformGuard>
              <PlatformLandingPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/billing"
          element={
            <PlatformGuard>
              <PlatformBillingSettingsPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/coupons"
          element={
            <PlatformGuard>
              <PlatformCouponsPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/reports"
          element={
            <PlatformGuard>
              <PlatformReportsPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/mail"
          element={
            <PlatformGuard>
              <PlatformMailPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/captcha"
          element={
            <PlatformGuard>
              <PlatformCaptchaPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/errors"
          element={
            <PlatformGuard>
              <PlatformErrorsPage />
            </PlatformGuard>
          }
        />
        <Route
          path="platform/privacy"
          element={
            <PlatformGuard>
              <PlatformPrivacyPage />
            </PlatformGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
